import { FunctionComponent } from "react";
import { IOrderDeliveryTask } from "../../../../interfaces/delivery-company/tasks";
import { Box, Paper, Typography, useMediaQuery, useTheme } from "@mui/material";
import { OrderDeliveryTaskState } from "../../../../enums/tasks";
import { get_cod } from "../../../../utils/cod";
import { add, displayMoney, minus, moneyToMetric, Money } from "../../../../utils/money";
import { Workflow } from "../../../../enums/orders";
import {
  compute_total_cod,
  compute_total_delivery_fee,
  compute_total_driver_commission_from_delivery_tasks,
  compute_total_pop,
} from "../../../common/report-builder/utils";
import { green, red } from "@mui/material/colors";
import { OrderType } from "../../../../interfaces/delivery-company/orders";

interface ExtraCharge {
  reason: string;
  amount: Money[];
  custom_reason?: string;
}

interface MerchantAmountsProps {
  orders: OrderType[];
  extraCharges?: ExtraCharge[];
}

const MerchantAmounts: FunctionComponent<MerchantAmountsProps> = ({
  orders,
  extraCharges = [],
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const totalStandardCOD = compute_total_cod(orders.filter(o => o.workflow == Workflow.STANDARD))
  const totalStandardDeliveryFee = compute_total_delivery_fee(orders.filter(o => o.workflow == Workflow.STANDARD))
  const totalStandardPOP = compute_total_pop(orders.filter(o => o.workflow == Workflow.STANDARD))

  const totalReturnCOD = compute_total_cod(orders.filter(o => o.workflow == Workflow.RETURN))

  // Calculate total extra charges
  const totalExtraCharges = extraCharges.reduce((acc, charge) => add(acc, charge.amount), [] as Money[])

  // Include extra charges in the calculation (they reduce what's paid to merchant)
  const totalNetDueToMerchant = minus(minus(minus(totalStandardCOD, add(totalStandardDeliveryFee, totalStandardPOP)), totalReturnCOD), totalExtraCharges)

  const netPositive = moneyToMetric(totalNetDueToMerchant).isPositive();

  const NetBox = (
    <Paper
      elevation={10}
      sx={{
        p: 2,
        minWidth: 200,
        bgcolor: netPositive ? green[100] : red[100],
        border: "2px solid",
        borderColor: netPositive ? green[500] : red[500],
        flexGrow: 1,
      }}
    >
      <Typography variant="subtitle2" color="text.secondary">
        Net to Pay to Merchant
      </Typography>
      <Typography variant="h5" fontWeight="bold">
        {displayMoney(totalNetDueToMerchant)}
      </Typography>
    </Paper>
  );

  const CodBox = (
    <Paper elevation={2} sx={{ p: 2, minWidth: 180, flexGrow: 1 }}>
      <Typography variant="subtitle2" color="text.secondary">
        Total COD
      </Typography>
      <Typography variant="h6">{displayMoney(totalStandardCOD)}</Typography>
    </Paper>
  );

  const DeliveryFeeBox = (
    <Paper elevation={2} sx={{ p: 2, minWidth: 180, flexGrow: 1 }}>
      <Typography variant="subtitle2" color="text.secondary">
        Total Delivery Fee
      </Typography>
      <Typography variant="h6">{displayMoney(totalStandardDeliveryFee)}</Typography>
    </Paper>
  );

  const PopBox = (
    <Paper elevation={2} sx={{ p: 2, minWidth: 180, flexGrow: 1 }}>
      <Typography variant="subtitle2" color="text.secondary">
        Total standard POP
      </Typography>
      <Typography variant="h6">{displayMoney(totalStandardPOP)}</Typography>
    </Paper>
  );



  const ReturnCodBox = (
    <Paper elevation={2} sx={{ p: 2, minWidth: 180, flexGrow: 1 }}>
      <Typography variant="subtitle2" color="text.secondary">
        Total Return Fees
      </Typography>
      <Typography variant="h6">{displayMoney(totalReturnCOD)}</Typography>
    </Paper>
  );

  const ExtraChargesBox = extraCharges.length > 0 ? (
    <Paper elevation={2} sx={{ p: 2, minWidth: 180, flexGrow: 1, bgcolor: red[50], border: '1px solid', borderColor: red[200] }}>
      <Typography variant="subtitle2" color="text.secondary">
        Total Extra Charges
      </Typography>
      <Typography variant="h6" color="error.main">{displayMoney(totalExtraCharges)}</Typography>
    </Paper>
  ) : null;

  

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        mt: 2,
        flexDirection: {
          xs: "column",
          sm: "row",
        },
        justifyContent: {
          xs: "center",
          sm: "flex-start",
        },
        alignItems: {
          xs: "stretch",
          sm: "flex-start",
        },
        flexWrap: "wrap",
      }}
    >
        <>
          {NetBox}
          {CodBox}
          {DeliveryFeeBox}
          {ReturnCodBox}
          {PopBox}
          {ExtraChargesBox}
        </>
    </Box>
  );
};

export default MerchantAmounts;
